import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { InvoiceAllocation, OpenInvoice } from './types';
import { getCustomerOpenInvoices, getDocumentDetails } from '../../api/client';

interface DocumentLineItem {
  description?: string;
  quantity: number;
  price_nis?: number;
  total_nis?: number;
}

interface DocumentVatSummary {
  subtotal?: number;
  vat_rate?: number;
  vat_amount?: number;
  total?: number;
}

interface InvoiceSelectionStepProps {
  customerId: number | null;
  allocations: InvoiceAllocation[];
  onChange: (allocations: InvoiceAllocation[]) => void;
  paidAmount?: number;
}

export default function InvoiceSelectionStep({
  customerId,
  allocations,
  onChange,
  paidAmount,
}: InvoiceSelectionStepProps) {
  const { t } = useTranslation();
  const [openInvoices, setOpenInvoices] = useState<OpenInvoice[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [unknownBalanceCount, setUnknownBalanceCount] = useState(0);
  const [distributeInput, setDistributeInput] = useState<string>('');
  const autoSuggestedRef = useRef(false);
  const [expandedInvoices, setExpandedInvoices] = useState<Set<string>>(new Set());
  const [invoiceLineItems, setInvoiceLineItems] = useState<Record<string, DocumentLineItem[]>>({});
  const [invoiceVatSummary, setInvoiceVatSummary] = useState<Record<string, DocumentVatSummary>>({});
  const [lineItemsLoading, setLineItemsLoading] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!customerId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    autoSuggestedRef.current = false;

    getCustomerOpenInvoices(customerId)
      .then((res) => {
        if (!cancelled) {
          const invoices = res.data || [];
          const knownInvoices: OpenInvoice[] = invoices
            .filter((invoice) => invoice.balance_status !== 'unknown' && invoice.open_balance != null)
            .map((invoice) => ({
              ...invoice,
              paid_amount: invoice.paid_amount || 0,
              open_balance: Number(invoice.open_balance),
            }));
          setUnknownBalanceCount(invoices.length - knownInvoices.length);
          setOpenInvoices(knownInvoices);

          // Auto-suggest allocations oldest-first when paidAmount is provided
          if (paidAmount && paidAmount > 0 && knownInvoices.length > 0) {
            const sorted = [...knownInvoices].sort(
              (a, b) => new Date(a.document_date).getTime() - new Date(b.document_date).getTime(),
            );
            let remaining = paidAmount;
            const suggested: InvoiceAllocation[] = [];
            for (const inv of sorted) {
              if (remaining <= 0) break;
              const closeAmount = Math.min(inv.open_balance, remaining);
              remaining = Math.max(0, remaining - closeAmount);
              if (closeAmount > 0) {
                suggested.push({
                  document_type: inv.document_type,
                  document_number: inv.document_number,
                  amount_close: Math.round(closeAmount * 100) / 100,
                  invoice_total: inv.amount,
                  open_balance: inv.open_balance,
                  document_date: inv.document_date,
                });
              }
            }
            onChange(suggested);
            autoSuggestedRef.current = true;
          }
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err?.message || 'Failed to load invoices');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [customerId]);

  const selectedSet = useMemo(() => {
    const s = new Map<string, InvoiceAllocation>();
    for (const a of allocations) {
      s.set(`${a.document_type}#${a.document_number}`, a);
    }
    return s;
  }, [allocations]);

  const allocatedTotal = useMemo(
    () => allocations.reduce((sum, a) => sum + (a.amount_close || 0), 0),
    [allocations],
  );

  // allocatedTotal is used in summary and passed to payments step via wizard data

  const toggleInvoice = useCallback(
    (inv: OpenInvoice) => {
      const key = `${inv.document_type}#${inv.document_number}`;
      if (selectedSet.has(key)) {
        onChange(allocations.filter(
          (a) => !(a.document_type === inv.document_type && a.document_number === inv.document_number),
        ));
      } else {
        onChange([
          ...allocations,
          {
            document_type: inv.document_type,
            document_number: inv.document_number,
            amount_close: inv.open_balance,
            invoice_total: inv.amount,
            open_balance: inv.open_balance,
            document_date: inv.document_date,
          },
        ]);
      }
    },
    [allocations, onChange, selectedSet],
  );

  const updateAmount = useCallback(
    (docType: number, docNumber: number, amount: number) => {
      onChange(
        allocations.map((a) =>
          a.document_type === docType && a.document_number === docNumber
            ? { ...a, amount_close: amount }
            : a,
        ),
      );
    },
    [allocations, onChange],
  );

  const autoAllocate = useCallback(() => {
    const newAllocations: InvoiceAllocation[] = openInvoices.map((inv) => ({
      document_type: inv.document_type,
      document_number: inv.document_number,
      amount_close: inv.open_balance,
      invoice_total: inv.amount,
      open_balance: inv.open_balance,
      document_date: inv.document_date,
    }));
    onChange(newAllocations);
  }, [openInvoices, onChange]);

  const distributeAmount = useCallback(() => {
    const total = parseFloat(distributeInput);
    if (!total || total <= 0) return;

    // Sort selected invoices oldest-first for allocation order; use open invoices order if none selected
    const baseInvoices = allocations.length > 0
      ? openInvoices.filter((inv) => allocations.some(
          (a) => a.document_type === inv.document_type && a.document_number === inv.document_number,
        ))
      : openInvoices;

    let remaining = total;
    const newAllocations: InvoiceAllocation[] = [];

    for (const inv of baseInvoices) {
      if (remaining <= 0) break;
      const closeAmount = Math.min(inv.open_balance, remaining);
      remaining = Math.max(0, remaining - closeAmount);
      if (closeAmount > 0) {
        newAllocations.push({
          document_type: inv.document_type,
          document_number: inv.document_number,
          amount_close: Math.round(closeAmount * 100) / 100,
          invoice_total: inv.amount,
          open_balance: inv.open_balance,
          document_date: inv.document_date,
        });
      }
    }
    onChange(newAllocations);
  }, [distributeInput, allocations, openInvoices, onChange]);

  const toggleInvoiceExpand = useCallback(async (inv: OpenInvoice) => {
    const key = `${inv.document_type}#${inv.document_number}`;
    if (expandedInvoices.has(key)) {
      setExpandedInvoices(prev => { const next = new Set(prev); next.delete(key); return next; });
      return;
    }
    setExpandedInvoices(prev => new Set(prev).add(key));
    if (invoiceLineItems[key]) return;
    setLineItemsLoading(prev => new Set(prev).add(key));
    try {
      const res = await getDocumentDetails(inv.document_type, inv.document_number);
      setInvoiceLineItems(prev => ({ ...prev, [key]: res.data.items }));
      setInvoiceVatSummary(prev => ({
        ...prev,
        [key]: {
          subtotal: res.data.subtotal,
          vat_rate: res.data.vat_rate,
          vat_amount: res.data.vat_amount,
          total: res.data.total,
        },
      }));
    } catch {
      setInvoiceLineItems(prev => ({ ...prev, [key]: [] }));
    } finally {
      setLineItemsLoading(prev => { const next = new Set(prev); next.delete(key); return next; });
    }
  }, [expandedInvoices, invoiceLineItems]);

  const getStatus = (alloc: InvoiceAllocation): string => {
    if (!alloc.open_balance) return t('createDocument.invoices.notSelected');
    if (Math.abs(alloc.amount_close - alloc.open_balance) < 0.01) {
      return t('createDocument.invoices.fullyClosed');
    }
    return t('createDocument.invoices.partiallyClosed');
  };

  const getStatusColor = (alloc: InvoiceAllocation): string => {
    if (!alloc.open_balance) return 'text-gray-500';
    if (Math.abs(alloc.amount_close - alloc.open_balance) < 0.01) {
      return 'text-green-600';
    }
    return 'text-orange-600';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12 text-gray-500">
        <span className="animate-spin mr-2">⏳</span>
        {t('createDocument.invoices.loading')}
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-red-600 text-sm">
        {error}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {/* Paid amount banner */}
      {paidAmount != null && paidAmount > 0 && (
        <div className="flex items-center justify-between rounded-xl border border-green-200 bg-green-50 p-4">
          <span className="text-sm font-semibold text-green-700">
            💰 {t('createDocument.invoices.paidAmount')}: ₪{paidAmount.toFixed(2)}
          </span>
          {allocatedTotal > 0 && (
            <span className={`text-sm font-medium ${
              Math.abs(allocatedTotal - paidAmount) < 0.01
                ? 'text-green-600'
                : allocatedTotal < paidAmount
                  ? 'text-orange-600'
                  : 'text-red-600'
            }`}>
              {t('createDocument.invoices.allocatedAmount')}: ₪{allocatedTotal.toFixed(2)}
            </span>
          )}
        </div>
      )}

      {unknownBalanceCount > 0 && (
        <div className="rounded-xl border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900" role="alert" data-testid="invoice-selection-balance-warning">
          {t(
            'createDocument.invoices.unknownBalanceWarning',
            '{{count}} invoice balance could not be verified and cannot be selected until balances refresh.',
            { count: unknownBalanceCount },
          )}
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-800">
            {t('createDocument.invoices.title')}
          </h3>
          <p className="text-sm text-gray-500 mt-1">
            {t('createDocument.invoices.subtitle')}
          </p>
        </div>
        {openInvoices.length > 0 && (
          <button
            type="button"
            onClick={autoAllocate}
            className="rounded-lg bg-blue-50 px-4 py-2 text-sm font-medium text-blue-700
              hover:bg-blue-100 transition-colors focus:outline-none focus:ring-2
              focus:ring-blue-300"
            title={t('createDocument.invoices.autoAllocateTooltip')}
          >
            🔄 {t('createDocument.invoices.autoAllocate')}
          </button>
        )}
      </div>

      {openInvoices.length === 0 ? (
        <div className="rounded-xl border-2 border-dashed border-gray-200 p-8 text-center text-gray-500">
          {t('createDocument.invoices.noOpenInvoices')}
        </div>
      ) : (
        <>
          {/* Invoice table */}
          <div className="rounded-xl border border-gray-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-3 py-3 text-start font-medium text-gray-500 w-8"></th>
                  <th className="px-3 py-3 text-start font-medium text-gray-500 w-10"></th>
                  <th className="px-3 py-3 text-start font-medium text-gray-500">
                    {t('createDocument.invoices.invoiceNumber')}
                  </th>
                  <th className="px-3 py-3 text-start font-medium text-gray-500">
                    {t('createDocument.invoices.date')}
                  </th>
                  <th className="px-3 py-3 text-end font-medium text-gray-500">
                    {t('createDocument.invoices.originalAmount')}
                  </th>
                  <th className="px-3 py-3 text-end font-medium text-gray-500">
                    {t('createDocument.invoices.openBalance')}
                  </th>
                  <th className="px-3 py-3 text-end font-medium text-gray-500 w-40">
                    {t('createDocument.invoices.amountToClose')}
                  </th>
                  <th className="px-3 py-3 text-start font-medium text-gray-500">
                    {t('createDocument.invoices.status')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {openInvoices.map((inv) => {
                  const key = `${inv.document_type}#${inv.document_number}`;
                  const alloc = selectedSet.get(key);
                  const isSelected = !!alloc;
                  const hasError = alloc && (
                    alloc.amount_close <= 0 ||
                    (alloc.open_balance && alloc.amount_close > alloc.open_balance + 0.01)
                  );
                  const isExpanded = expandedInvoices.has(key);
                  const isLoadingItems = lineItemsLoading.has(key);
                  const lineItems = invoiceLineItems[key];

                  return (
                    <React.Fragment key={key}>
                      <tr className={`transition-colors ${isSelected ? 'bg-blue-50/50' : 'hover:bg-gray-50'}`}>
                        <td className="px-2 py-3 text-center">
                          <button
                            type="button"
                            onClick={() => toggleInvoiceExpand(inv)}
                            className="inline-flex items-center justify-center w-5 h-5 rounded hover:bg-gray-200 transition-colors text-gray-400"
                            aria-label={isExpanded ? 'Collapse' : 'Expand'}
                          >
                            {isLoadingItems ? (
                              <span className="inline-block w-3 h-3 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin" />
                            ) : (
                              <span className={`inline-block text-xs transition-transform ${isExpanded ? 'rotate-90' : ''}`}>▶</span>
                            )}
                          </button>
                        </td>
                        <td className="px-3 py-3">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleInvoice(inv)}
                            className="h-4 w-4 rounded border-gray-300 text-blue-600
                              focus:ring-blue-500 cursor-pointer"
                          />
                        </td>
                        <td className="px-3 py-3 font-medium text-gray-800">
                          {inv.document_number}
                        </td>
                        <td className="px-3 py-3 text-gray-600">
                          {inv.document_date}
                        </td>
                        <td className="px-3 py-3 text-end text-gray-600">
                          ₪{inv.amount.toFixed(2)}
                        </td>
                        <td className="px-3 py-3 text-end font-medium text-gray-800">
                          ₪{inv.open_balance.toFixed(2)}
                        </td>
                        <td className="px-3 py-3 text-end">
                          {isSelected ? (
                            <input
                              type="number"
                              min={0.01}
                              max={inv.open_balance}
                              step={0.01}
                              value={alloc.amount_close || ''}
                              onChange={(e) =>
                                updateAmount(
                                  inv.document_type,
                                  inv.document_number,
                                  parseFloat(e.target.value) || 0,
                                )
                              }
                              className={`w-full rounded-lg border p-2 text-end text-sm font-medium
                                focus:outline-none focus:ring-2 transition-all
                                ${hasError
                                  ? 'border-red-300 focus:ring-red-200 text-red-700'
                                  : 'border-gray-300 focus:ring-blue-200 text-gray-800'
                                }`}
                            />
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </td>
                        <td className="px-3 py-3">
                          {isSelected ? (
                            <span className={`text-xs font-semibold ${getStatusColor(alloc)}`}>
                              {getStatus(alloc)}
                            </span>
                          ) : (
                            <span className="text-xs text-gray-400">
                              {t('createDocument.invoices.notSelected')}
                            </span>
                          )}
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr>
                          <td colSpan={8} className="p-0 bg-gray-50/60">
                            {isLoadingItems ? (
                              <div className="px-10 py-3 text-xs text-gray-400">
                                {t('documents.loadingItems', 'Loading items...')}
                              </div>
                            ) : lineItems && lineItems.length > 0 ? (
                              <table className="w-full text-xs">
                                <thead>
                                  <tr className="bg-gray-100/60 text-gray-400 uppercase tracking-wider">
                                    <th className="w-10 px-2 py-1.5" />
                                    <th className="px-4 py-1.5 text-start font-medium">
                                      {t('documents.itemDescription', 'Description')}
                                    </th>
                                    <th className="px-4 py-1.5 text-end font-medium">
                                      {t('documents.itemQuantity', 'Qty')}
                                    </th>
                                    <th className="px-4 py-1.5 text-end font-medium">
                                      {t('documents.itemPrice', 'Price')}
                                    </th>
                                    <th className="px-4 py-1.5 text-end font-medium">
                                      {t('documents.itemTotal', 'Total')}
                                    </th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-50">
                                  {lineItems.map((item, idx) => (
                                    <tr key={idx} className="bg-white/60 hover:bg-gray-50/80 transition-colors">
                                      <td className="w-10 px-2 py-1.5" />
                                      <td className="px-4 py-1.5 text-gray-800">{item.description || '—'}</td>
                                      <td className="px-4 py-1.5 text-end text-gray-600">{item.quantity}</td>
                                      <td className="px-4 py-1.5 text-end text-gray-600">
                                        {item.price_nis != null ? `₪${item.price_nis.toFixed(2)}` : '—'}
                                      </td>
                                      <td className="px-4 py-1.5 text-end font-medium text-gray-800">
                                        {item.total_nis != null ? `₪${item.total_nis.toFixed(2)}` : '—'}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                                {(() => {
                                  const vatInfo = invoiceVatSummary[key];
                                  if (!vatInfo || (vatInfo.subtotal == null && vatInfo.total == null)) return null;
                                  return (
                                    <tfoot className="border-t border-gray-200 bg-gray-50/80">
                                      {vatInfo.subtotal != null && (
                                        <tr>
                                          <td className="w-10 px-2 py-1" />
                                          <td colSpan={3} className="px-4 py-1 text-end text-gray-500">
                                            {t('documents.subtotal', 'Subtotal')}
                                          </td>
                                          <td className="px-4 py-1 text-end text-gray-600">
                                            ₪{vatInfo.subtotal.toFixed(2)}
                                          </td>
                                        </tr>
                                      )}
                                      {vatInfo.vat_amount != null && (
                                        <tr>
                                          <td className="w-10 px-2 py-1" />
                                          <td colSpan={3} className="px-4 py-1 text-end text-gray-500">
                                            {t('documents.vat', 'VAT')}{vatInfo.vat_rate != null ? ` (${vatInfo.vat_rate}%)` : ''}
                                          </td>
                                          <td className="px-4 py-1 text-end text-gray-600">
                                            ₪{vatInfo.vat_amount.toFixed(2)}
                                          </td>
                                        </tr>
                                      )}
                                      {vatInfo.total != null && (
                                        <tr>
                                          <td className="w-10 px-2 py-1.5" />
                                          <td colSpan={3} className="px-4 py-1.5 text-end font-semibold text-gray-700">
                                            {t('documents.totalWithVat', 'Total (incl. VAT)')}
                                          </td>
                                          <td className="px-4 py-1.5 text-end font-semibold text-gray-800">
                                            ₪{vatInfo.total.toFixed(2)}
                                          </td>
                                        </tr>
                                      )}
                                    </tfoot>
                                  );
                                })()}
                              </table>
                            ) : (
                              <div className="px-10 py-2 text-xs text-gray-400">
                                {t('documents.noItems', 'No line items')}
                              </div>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Allocation summary */}
          {allocations.length > 0 && (
            <div className="flex items-center justify-between rounded-xl border p-4 text-blue-600 bg-blue-50 border-blue-200">
              <span className="text-sm font-semibold">
                {t('createDocument.invoices.allocatedAmount')}: ₪{allocatedTotal.toFixed(2)}
              </span>
              <span className="text-xs text-gray-500">
                {allocations.length} {t('createDocument.invoices.invoicesSelected')}
              </span>
            </div>
          )}

          {/* Distribute by payment amount */}
          <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
            <p className="text-sm font-medium text-gray-700 mb-2">
              {t('createDocument.invoices.distributeHint')}
            </p>
            <div className="flex gap-2">
              <input
                type="number"
                min={0.01}
                step={0.01}
                placeholder="0.00"
                value={distributeInput}
                onChange={(e) => setDistributeInput(e.target.value)}
                className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm
                  focus:outline-none focus:ring-2 focus:ring-blue-200 text-gray-800"
              />
              <button
                type="button"
                onClick={distributeAmount}
                disabled={!distributeInput || parseFloat(distributeInput) <= 0}
                className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white
                  hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed
                  transition-colors focus:outline-none focus:ring-2 focus:ring-blue-300"
              >
                {t('createDocument.invoices.distributeButton')}
              </button>
            </div>
          </div>
        </>
      )}

      {/* Skip option */}
      {allocations.length === 0 && openInvoices.length > 0 && (
        <p className="text-center text-xs text-gray-400 mt-2">
          {t('createDocument.invoices.skipInvoices')}
        </p>
      )}
    </div>
  );
}
